import { useEffect, useSyncExternalStore } from 'react';

/**
 * «La lente è spenta adesso?», in una forma sola per tutta l'app.
 *
 * La lente (il pinch a molla di `useLente`) ingrandisce la pagina finché si
 * tengono le dita sullo schermo. Dove la scacchiera occupa tutto lo schermo non
 * deve fare nulla: la scacchiera è GIÀ dimensionata sulla viewport, non c'è un
 * dettaglio piccolo da leggere che l'ingrandimento sveli, e un secondo dito che
 * fa saltare la vista mentre si trascina un pezzo è solo un modo per sbagliare
 * mossa.
 *
 * Due forme, una verità — lo schema è quello di `usePaginaNascosta`:
 *  - `lenteBloccata()` — imperativa, per il gestore del gesto, che vive fuori da
 *    React e legge lo stato dentro un ascoltatore di `touchstart`;
 *  - `useBloccoLente()` — dichiarativa, per i componenti: chiamarlo blocca la
 *    lente per tutta la vita del componente e la sblocca allo smontaggio.
 *
 * ⚠️ Un CONTATORE, non un booleano. Le superfici che bloccano si sovrappongono:
 * `DashboardPuzzleModal` monta `PuzzlePage` SOPRA una schermata di partita, e
 * quindi due componenti bloccano insieme. Con un booleano, chiudere la modale
 * scriverebbe `false` e riaccenderebbe la lente sulla partita rimasta sotto —
 * un difetto che si vede solo in quella sequenza, cioè quasi mai in prova e
 * sempre in mano a chi gioca.
 */

let blocchi = 0;
const iscritti = new Set<() => void>();

function avvisaTutti(): void {
  for (const avvisa of iscritti) avvisa();
}

/** Lo stato adesso, per chi sta fuori da React. */
export function lenteBloccata(): boolean {
  return blocchi > 0;
}

function iscriviti(avvisa: () => void): () => void {
  iscritti.add(avvisa);
  return () => {
    iscritti.delete(avvisa);
  };
}

/**
 * Blocca la lente finché il componente resta montato.
 *
 * Va chiamato in cima al componente, come ogni hook: le pagine che lo usano
 * hanno uscite anticipate (`PuzzlePage` mostra `PuzzleUnavailable` quando il
 * problema non c'è) e una chiamata sotto quel `return` sarebbe un hook in meno
 * nel ramo sbagliato.
 */
export function useBloccoLente(): void {
  useEffect(() => {
    blocchi += 1;
    if (blocchi === 1) avvisaTutti();
    return () => {
      blocchi -= 1;
      if (blocchi === 0) avvisaTutti();
    };
  }, []);
}

/** Lo stato adesso, reattivo. Serve ai componenti che vogliono ADATTARSI al blocco. */
export function useLenteBloccata(): boolean {
  return useSyncExternalStore(iscriviti, lenteBloccata, () => false);
}

/** Solo per i test: riporta il contatore a zero fra un caso e l'altro. */
export function azzeraBlocchiLente(): void {
  blocchi = 0;
  avvisaTutti();
}
